import { CLIENT_QUERY } from '@/router/query-params'

const SESSION_STORAGE_KEY = 'pg_dsp_click_attribution'
const BROWSER_STORAGE_KEY = 'pg_dsp_click_browser_attribution'

type StoredDspClickAttribution = {
    clickId: string
    sessionId?: string
}

function isBrowser() {
    return typeof window !== 'undefined'
}

function normalizeClickId(value: string | null | undefined): string | undefined {
    const clickId = value?.trim()
    if (!clickId || clickId.length > 128 || !/^[A-Za-z0-9_-]+$/.test(clickId)) {
        return undefined
    }
    return clickId
}

function readClickIdFromUrl(): string | undefined {
    if (!isBrowser()) {
        return undefined
    }
    const params = new URLSearchParams(window.location.search)
    return (
        normalizeClickId(params.get(CLIENT_QUERY.DSP_CLICK_ID)) ??
        normalizeClickId(params.get(CLIENT_QUERY.LEGACY_DSP_CLICK_ID))
    )
}

function readStored(storageKey: string): StoredDspClickAttribution | undefined {
    if (!isBrowser()) {
        return undefined
    }
    try {
        const raw = readStorageItem(storageKey)
        if (!raw) {
            return undefined
        }
        const parsed = JSON.parse(raw) as StoredDspClickAttribution
        const clickId = normalizeClickId(parsed.clickId)
        if (!clickId) {
            removeStored(storageKey)
            return undefined
        }
        return {
            clickId,
            sessionId: typeof parsed.sessionId === 'string' ? parsed.sessionId : undefined,
        }
    } catch {
        removeStored(storageKey)
        return undefined
    }
}

function readStorageItem(storageKey: string): string | null {
    try {
        return window.sessionStorage.getItem(storageKey)
    } catch {
        return null
    }
}

function writeStored(storageKey: string, clickId: string, sessionId?: string) {
    if (!isBrowser()) {
        return
    }
    try {
        window.sessionStorage.setItem(storageKey, JSON.stringify({ clickId, sessionId }))
    } catch {
        // Storage is best-effort; attribution must not block event delivery.
    }
}

function removeStored(storageKey: string) {
    if (!isBrowser()) {
        return
    }
    try {
        window.sessionStorage.removeItem(storageKey)
    } catch {
        // Storage is best-effort; attribution must not block event delivery.
    }
}

export function getDspClickIdForSession(sessionId: string): string | undefined {
    const clickIdFromUrl = readClickIdFromUrl()
    if (clickIdFromUrl) {
        writeStored(SESSION_STORAGE_KEY, clickIdFromUrl, sessionId)
        writeStored(BROWSER_STORAGE_KEY, clickIdFromUrl)
        return clickIdFromUrl
    }

    const stored = readStored(SESSION_STORAGE_KEY)
    if (!stored) {
        return undefined
    }
    if (stored.sessionId === sessionId) {
        return stored.clickId
    }
    removeStored(SESSION_STORAGE_KEY)
    return undefined
}

export function getDspClickIdForBrowserSession(): string | undefined {
    const clickIdFromUrl = readClickIdFromUrl()
    if (clickIdFromUrl) {
        writeStored(BROWSER_STORAGE_KEY, clickIdFromUrl)
        return clickIdFromUrl
    }
    return readStored(BROWSER_STORAGE_KEY)?.clickId
}
